import { consola } from 'consola'
import type { Order, OrderStatus, PaymentStatus, OrderType, PaymentMethod } from '@entities/order'
import { PAYMENT_STATUSES } from '@shared/config'

/**
 * Константы статусов заказов
 */
export const ORDER_STATUS_CONSTANTS = {
  COMPLETED_STATUSES: ['confirmed', 'preparing', 'ready', 'delivery', 'delivered'] as const,
  CANCELLED_STATUS: 'cancelled' as const,
  PENDING_STATUS: 'pending' as const,
  PAID_PAYMENT_STATUS: 'paid' as const,
  UNPAID_PAYMENT_STATUSES: [PAYMENT_STATUSES.WAITING, PAYMENT_STATUSES.PENDING] as const,
} as const

/**
 * Проверяет, является ли заказ неоплаченным
 * @param order - объект заказа
 * @returns true если заказ неоплачен
 */
export function isOrderUnpaid(order: Order | null): boolean {
  consola.debug('🔍 isOrderUnpaid called with order:', {
    orderExists: !!order,
    orderId: order?.id,
    orderStatus: order?.status,
    paymentStatus: order?.payment_status,
  })
  
  if (!order || !order.id) {
    consola.debug('🔍 isOrderUnpaid: no order or order.id, returning false')
    return false
  }
  
  // Если явно оплачен
  if (order.payment_status === ORDER_STATUS_CONSTANTS.PAID_PAYMENT_STATUS) {
    consola.debug('🔍 isOrderUnpaid: payment_status is paid, returning false')
    return false
  }
  
  // Если заказ уже в процессе выполнения или завершен - значит оплачен
  if (ORDER_STATUS_CONSTANTS.COMPLETED_STATUSES.includes(order.status as any)) {
    consola.debug('🔍 isOrderUnpaid: order status is completed, returning false')
    return false
  }
  
  // Если заказ отменен - не считаем неоплаченным
  if (order.status === ORDER_STATUS_CONSTANTS.CANCELLED_STATUS) {
    consola.debug('🔍 isOrderUnpaid: order status is cancelled, returning false')
    return false
  }
  
  const paymentStatus = (order.payment_status || (order as any).paymentStatus) as PaymentStatus | undefined
  const result = (paymentStatus && ORDER_STATUS_CONSTANTS.UNPAID_PAYMENT_STATUSES.includes(paymentStatus)) ||
                 (!paymentStatus && order.status === ORDER_STATUS_CONSTANTS.PENDING_STATUS)
  consola.debug('🔍 isOrderUnpaid final result:', result, 'order:', order?.id)
  return result
}

/**
 * Проверяет, можно ли повторить оплату заказа
 * @param order - объект заказа
 * @param isTokenExpired - флаг истечения токена
 * @returns true если можно повторить оплату
 */
export function canRetryOrderPayment(order: Order | null, isTokenExpired: boolean = false): boolean {
  if (!order) return false
  
  // Если заказ явно оплачен
  if (order.payment_status === ORDER_STATUS_CONSTANTS.PAID_PAYMENT_STATUS) return false
  
  // Если заказ уже в процессе выполнения или завершен - значит оплачен
  if (ORDER_STATUS_CONSTANTS.COMPLETED_STATUSES.includes(order.status as any)) return false
  
  // Если заказ отменен
  if (order.status === ORDER_STATUS_CONSTANTS.CANCELLED_STATUS) return false
  
  const paymentStatus = (order.payment_status || (order as any).paymentStatus) as PaymentStatus | undefined
  const isUnpaid = (paymentStatus && ORDER_STATUS_CONSTANTS.UNPAID_PAYMENT_STATUSES.includes(paymentStatus)) ||
                   (!paymentStatus && order.status === ORDER_STATUS_CONSTANTS.PENDING_STATUS)
  
  if (!isUnpaid) return false
  
  // Если токен истек, нельзя повторить оплату
  if (isTokenExpired) return false
  
  return true
}

/**
 * Проверяет, является ли заказ завершенным (оплаченным или в процессе выполнения)
 * @param order - объект заказа
 * @returns true если заказ завершен
 */
export function isOrderCompleted(order: Order | null): boolean {
  if (!order) return false
  
  return order.payment_status === ORDER_STATUS_CONSTANTS.PAID_PAYMENT_STATUS ||
         ORDER_STATUS_CONSTANTS.COMPLETED_STATUSES.includes(order.status as any)
}

/**
 * Проверяет, является ли заказ отмененным
 * @param order - объект заказа
 * @returns true если заказ отменен
 */
export function isOrderCancelled(order: Order | null): boolean {
  if (!order) return false
  return order.status === ORDER_STATUS_CONSTANTS.CANCELLED_STATUS
}

/**
 * Проверяет, произошло ли значительное изменение статуса заказа
 * @param oldStatus - старый статус заказа
 * @param newStatus - новый статус заказа
 * @param oldPaymentStatus - старый статус оплаты
 * @param newPaymentStatus - новый статус оплаты
 * @returns true если произошло значительное изменение
 */
export function hasSignificantStatusChange(
  oldStatus: string, 
  newStatus: string, 
  oldPaymentStatus: string, 
  newPaymentStatus: string,
): boolean {
  const paymentStatusChanged = oldPaymentStatus !== ORDER_STATUS_CONSTANTS.PAID_PAYMENT_STATUS && 
                              newPaymentStatus === ORDER_STATUS_CONSTANTS.PAID_PAYMENT_STATUS
  const orderStatusChanged = oldStatus === ORDER_STATUS_CONSTANTS.PENDING_STATUS && 
                            newStatus !== ORDER_STATUS_CONSTANTS.PENDING_STATUS
  
  return paymentStatusChanged || orderStatusChanged
}

/**
 * Создает минимальный объект заказа из базовых данных
 * @param orderId - ID заказа
 * @param status - статус заказа
 * @param paymentStatus - статус оплаты
 * @returns объект заказа с минимальными данными
 */
export function createMinimalOrder(
  orderId: string | number,
  status: string,
  paymentStatus: string,
): Order {
  return {
    id: orderId,
    status: status as OrderStatus,
    payment_status: paymentStatus as PaymentStatus,
    order_type: 'takeaway' as OrderType,
    payment_method: 'card' as PaymentMethod,
    total_amount: 0,
    service_fee: 0,
    delivery_cost: 0,
    points_used: 0,
    points_earned: 0,
    items: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }
}

/**
 * Обновляет статусы заказа в существующем объекте
 * @param order - исходный заказ
 * @param status - новый статус заказа
 * @param paymentStatus - новый статус оплаты
 * @returns обновленный заказ
 */
export function updateOrderStatuses(order: Order, status: string, paymentStatus: string): Order {
  return {
    ...order,
    status: status as OrderStatus,
    payment_status: paymentStatus as PaymentStatus,
    updated_at: new Date().toISOString(),
  }
}

/**
 * Проверяет валидность объекта заказа
 * @param order - объект заказа для проверки
 * @returns true если заказ валиден
 */
export function isValidOrder(order: any): order is Order {
  return order && 
         typeof order === 'object' && 
         (typeof order.id === 'string' || typeof order.id === 'number') &&
         typeof order.status === 'string' &&
         typeof order.payment_status === 'string'
}

/**
 * Логирует информацию о заказе для отладки
 * @param order - заказ для логирования
 * @param context - контекст логирования
 */
export function logOrderDebugInfo(order: Order | null, context: string): void {
  if (!order) {
    consola.debug(`🔄 ${context}: No order available`)
    return
  }
  
  consola.debug(`🔄 ${context}:`, {
    id: order.id,
    status: order.status,
    payment_status: order.payment_status,
    items_count: order.items?.length || 0,
    total_amount: order.total_amount,
    created_at: order.created_at,
  })
}